<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from "vue-router";
import { useLoadingStore } from "@/stores/loading";
import { downloadResultPDF } from "@/utils/pdfDownloader.js";
import api from "@/lib/api.js";

// AI 리터러시 학생 결과지 컴포넌트 import
import StudentAiLiteracyComponent from './student/index.vue';
import exam from '@/router/modules/exam';

    const router = useRouter();
    const route = useRoute();

    const shouldShowLayout = computed(() => {       
        const allowedChildNames = ['ResultAiStudent','ResultAiTeacher','ResultAiParent','ResultAiPrincipal','ResultStatusAI','ResultAiSchoolStatus'];
        return route.name && allowedChildNames.includes(route.name);
    });

    // 로딩 관련
    const loadingStore = useLoadingStore();
    const isReportReadyForDownload = ref(true);
    const downloadProgressMessage = ref('');
    
    // 다운로드 진행 상황 관리
    const totalStudentsCount = ref(0);
    const completedCount = ref(0);
    const currentProcessingStudent = ref('');
    const showProgressDetails = ref(false);

    // 네비게이션 함수들
    const navigateToStudent = () => {
        router.push({
            name: "ResultAiStudent",
            query: route.query, // 기존 쿼리 파라미터 유지
        });
    };
    const navigateToTeacher = () => {
        router.push({
            name: "ResultAiTeacher",
            query: route.query, // 기존 쿼리 파라미터 유지
        });
    };
    const navigateToPrincipal = () => {
        router.push({
            name: "ResultAiPrincipal",
            query: route.query, // 기존 쿼리 파라미터 유지
        });
    };
    const navigateToSchoolStatus = () => {
        router.push({
            name: "ResultAiSchoolStatus",
            query: route.query, // 기존 쿼리 파라미터 유지
        });
    };
    const navigateToAIStatus = () => {
        router.push({
            name: "ResultStatusAI",
            query: route.query, // 기존 쿼리 파라미터 유지
        });
    };

    // 선택된 상태 computed
    const isStudentSelected = computed(() => {
        return route.name === "ResultAiStudent";
    });
    const isTeacherSelected = computed(() => {
        return route.name === "ResultAiTeacher";
    });
    const isPrincipalSelected = computed(() => {
        return route.name === "ResultAiPrincipal";
    });
    const isResultStatusAISelected = computed(() => {
        return route.name === "ResultStatusAI";
    });
    const isSchoolStatusSelected = computed(() => {
        return route.name === "ResultAiSchoolStatus";
    });

    // 다운로드 함수 - Status 페이지 전체 학생 일괄 다운로드
    const triggerReportDownload = async () => {
        try {

            loadingStore.startDownloading();

            // Status 페이지인 경우 전체 학생 일괄 다운로드
            if (route.name === "ResultStatusAI") {
                await handleBulkStudentPdfDownload();
            } else {
                // 다른 페이지는 개별 다운로드
                alert('AI 리터러시 개별 다운로드 기능 개발 예정');
            }

            loadingStore.stopDownloading();
        } catch (error) {
            console.error("AI 리터러시 PDF 다운로드 실패:", error);
            loadingStore.stopDownloading();
            alert("PDF 생성 중 오류가 발생했습니다. 다시 시도해주세요.");
        }
    };

    // 가상의 student/index.vue 컴포넌트들 관리
    const currentStudentData = ref(null);
    const hiddenStudentComponentRef = ref(null);

    // Status 페이지에서 전체 학생 AI 리터러시 일괄 PDF 다운로드 처리 - 가상 컴포넌트 방식 + CSS 강제 적용
    const handleBulkStudentPdfDownload = async () => {
        try {

            // StatusAI.vue에서 사용하는 데이터 구조 확인
            if (!window.currentClassData || !window.currentClassData.list) {
                alert("학생 리스트 데이터를 찾을 수 없습니다.");
                return;
            }

            const { exam_id } = route.query;
            const studentList = window.currentClassData.list;
            
            // 탈퇴학생 제외한 실제 처리할 학생 수 계산
            const validStudents = studentList.filter(student => student.name !== '탈퇴학생');
            totalStudentsCount.value = validStudents.length;
            completedCount.value = 0;
            showProgressDetails.value = true;
            isReportReadyForDownload.value = false;
            
            let successCount = 0;
            let failCount = 0;

            // 각 학생별로 가상 컴포넌트를 만들어서 PDF 생성
            let processedIndex = 0;
            for (let i = 0; i < studentList.length; i++) {
                const student = studentList[i];
                
                // 탈퇴학생인 경우 건너뛰기
                if (student.name === '탈퇴학생') {
                    console.log(`탈퇴학생 건너뛰기: ${student.name}`);
                    continue;
                }
                
                processedIndex++;
                currentProcessingStudent.value = student.name;
                downloadProgressMessage.value = `${student.name} 처리 중... (${processedIndex}/${totalStudentsCount.value})`;
                
                try {
                    console.log(`${processedIndex}/${totalStudentsCount.value} - 학생 ${student.name} 처리 중...`);

                    // AI 리터러시 학생별 API 호출로 데이터 로드
                    const response = await api.post('/exam/stu/resultLitDetail', {
                        exam_id: exam_id,
                        user_id: student.user_id,
                    });

                    if (response.data.success) {
                        const studentData = response.data.result;
                        
                        // 가상의 student/index.vue 컴포넌트에 데이터 설정
                        currentStudentData.value = studentData;

                        // 컴포넌트 렌더링 및 완전한 로딩 대기
                        await new Promise(resolve => setTimeout(resolve, 2000));

                        // CSS 강제 적용 - 개별 다운로드와 동일하게 만들기
                        const hiddenContainer = document.querySelector('.hidden-pdf-container');
                        if (hiddenContainer) {
                            // 실제 페이지의 CSS를 복사해서 적용
                            const realContainer = document.querySelector('.result-common-wrapper .result-container');
                            if (realContainer) {
                                const realStyles = window.getComputedStyle(realContainer);
                                hiddenContainer.style.fontSize = realStyles.fontSize;
                                hiddenContainer.style.fontFamily = realStyles.fontFamily;
                                hiddenContainer.style.lineHeight = realStyles.lineHeight;
                                hiddenContainer.style.color = realStyles.color;
                            }
                            
                            // CSS 변수도 강제로 설정
                            hiddenContainer.style.setProperty('--result-ai-primary-800', '#566391');
                            hiddenContainer.style.setProperty('--result-ai-border', '#C7C9D5');
                            hiddenContainer.style.setProperty('--result-ai-primary-50', '#EAF6FE');
                        }

                        // student/index.vue의 triggerReportDownload 함수를 직접 호출
                        if (hiddenStudentComponentRef.value?.triggerReportDownload) {
                            await hiddenStudentComponentRef.value.triggerReportDownload(student.user_id);
                            console.log(`학생 ${student.name} PDF 다운로드 완료`);
                            successCount++;
                            completedCount.value++;
                        } else {
                            throw new Error("triggerReportDownload 함수를 찾을 수 없습니다.");
                        }

                        // 다음 학생을 위한 정리
                        currentStudentData.value = null;
                        await new Promise(resolve => setTimeout(resolve, 500));

                    } else {
                        throw new Error(response.data.message || "학생 데이터 로드 실패");
                    }
                } catch (error) {
                    console.error(`학생 ${student.name} 처리 실패:`, error);
                    failCount++;
                    completedCount.value++; // 실패해도 처리된 것으로 카운트
                    continue;
                }
            }

            console.log(`일괄 다운로드 완료 - 성공: ${successCount}, 실패: ${failCount}`);

            if (successCount > 0) {
                downloadProgressMessage.value = `다운로드 완료! 성공: ${successCount}명, 실패: ${failCount}명`;
                await new Promise(resolve => setTimeout(resolve, 2000));
                alert(`AI 리터러시 일괄 PDF 다운로드 완료!\n성공: ${successCount}명, 실패: ${failCount}명`);
            } else {
                downloadProgressMessage.value = "다운로드에 실패했습니다.";
                await new Promise(resolve => setTimeout(resolve, 1000));
                alert("AI 리터러시 검사결과지 일괄 다운로드에 실패했습니다.");
            }

        } catch (error) {
            console.error("AI 리터러시 일괄 PDF 다운로드 실패:", error);
            downloadProgressMessage.value = "오류가 발생했습니다.";
            await new Promise(resolve => setTimeout(resolve, 1000));
            alert("AI 리터러시 일괄 다운로드 중 오류가 발생했습니다.");
        } finally {
            currentStudentData.value = null;
            showProgressDetails.value = false;
            isReportReadyForDownload.value = true;
            downloadProgressMessage.value = '';
            currentProcessingStudent.value = '';
            totalStudentsCount.value = 0;
            completedCount.value = 0;
        }
    };

    const status = computed(() => {
        if(route.name === 'ResultStatusAI'){
            return {
                mainContent: '전체 학생 검사결과지 일괄 다운로드',
                subMessage: '전체 점수 및 성취 수준, 하위 영역별 점수 및 상/중/하 정보를 제공합니다.'
            };
        } else {
            return {
                mainContent: '잘못 접근 페이지입니다.',
                subMessage: '죄송합니다. 올바른 페이지로 이동해주세요.'      
            };            
        }
    });

</script>

<template>
    <div class="top-area pt-5">
        <!-- 학급용/상태 현황 버튼 -->
        <div
            v-if="route.name === 'ResultAiTeacher' || route.name === 'ResultStatusAI'"
            class="button-wrap pb-3"
        >
            <p
                @click="navigateToTeacher"
                :class="{ selected: isTeacherSelected }"
            >
                학급용
            </p>
            <p
                @click="navigateToAIStatus"
                :class="{ selected: isResultStatusAISelected }"
            >
                우리 반 하위 영역별 점수 현황
            </p>
        </div>

        <!-- 교장용 버튼 -->
        <div 
            v-if="route.name === 'ResultAiPrincipal' || route.name === 'ResultAiSchoolStatus'"
            class="button-wrap pb-3"
        >
            <p
                @click="navigateToPrincipal"
                :class="{ selected: isPrincipalSelected }"
            >
                학교장용
            </p>
            <p
                @click="navigateToSchoolStatus"
                :class="{ selected: isSchoolStatusSelected }"
            >
                우리 학교 반별 진단결과
            </p>
        </div>

        <!-- 로딩/다운로드 영역 -->
        <div class="loading">
            <div v-if="route.name == 'ResultStatusAI'" class="loading-overlay">
                <div class="loading-content">
                    <div class="top-text pb-4">
                        <h4>{{ status.mainContent }}</h4>
                        <p class="sub-message pt-2">
                            {{ status.subMessage }}
                        </p>
                    </div>
                    <div class="loading-message" v-if="!isReportReadyForDownload">
                        <p class="pb-2">{{ downloadProgressMessage }}</p>
                        
                        <!-- 진행 상황 상세 표시 -->
                        <div v-if="showProgressDetails" class="progress-details pb-3">
                            <div class="progress-counter">
                                <span class="completed">{{ completedCount }}</span> / 
                                <span class="total">{{ totalStudentsCount }}</span>명 처리 완료
                            </div>
                            
                            <!-- 도트 진행 표시 -->
                            <div class="progress-dots pt-2">
                                <span 
                                    v-for="(student, index) in totalStudentsCount" 
                                    :key="index"
                                    class="dot"
                                    :class="{
                                        'completed': index < completedCount,
                                        'current': index === completedCount && completedCount < totalStudentsCount
                                    }"
                                ></span>
                            </div>
                        </div>
                        
                        <div class="loader" aria-label="Loading">
                            <svg viewBox="0 0 100 100" aria-hidden="true">
                                <circle cx="50" cy="50" r="40" />
                                <circle class="top" cx="50" cy="50" r="40" />
                            </svg>
                        </div>
                        <p class="pt-2">
                            <span class="mark">!</span> 파일 용량이 크므로 생성에 약 10초
                            소요될 수 있습니다. <br />준비가 완료되면 버튼이 활성화 됩니다.
                        </p>
                    </div>
                    <button
                        v-show="isReportReadyForDownload"
                        @click="triggerReportDownload"
                        class="download-button"
                        :disabled="loadingStore.isDownloading"
                    >
                        AI 리터러시 검사결과지 PDF 다운로드
                    </button>
                </div>
            </div>
        </div>
    </div>

    <div v-if="shouldShowLayout" class="result-common-wrapper">
        <main class="result-container">            
            <router-view />
        </main>       
    </div>   

    <!-- 가상의 숨겨진 student/index.vue 컴포넌트 - CSS 강제 적용으로 개별 다운로드와 동일하게 -->
    <div 
        v-if="currentStudentData" 
        class="hidden-pdf-container result-common-wrapper"
        style="position: fixed; top: 20px; left: 20px; z-index: 9999; width: 1130px; opacity: 0; pointer-events: none; background: white;"
    >
        <main class="result-container">
            <div :class="`hidden-student-${currentStudentData.user_id || 'temp'}`">
                <StudentAiLiteracyComponent
                    ref="hiddenStudentComponentRef"
                    :studentData="currentStudentData"
                />
            </div>
        </main>
    </div>   
</template>

<style lang="scss" scoped>
    .top-area {
        width: 1130px;
        margin: 0 auto;
        position: relative;
        z-index: 20;
        
        .button-wrap {
            display: flex;
            gap: 5px;
            
            p {
                border: 1px solid #ccc;
                background-color: #fff;
                padding: 3px 12px;
                border-radius: 5px;
                font-size: 16px;
                cursor: pointer;
                
                &.selected {
                    color: #fff;
                    background-color: #333;
                }
            }
        }
    }
    
    .loading {
        .loading-overlay {
            width: 1130px;
            padding: 25px 0 30px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            background-color: rgba(36, 124, 255, 0.05);
            border-radius: 15px;
            font-size: 18px;
        }
        .loading-message {
            display: flex;
            flex-direction: column;
            align-items: center;
            .mark {
                display: inline-block;
                border: 1px solid #000;
                border-radius: 100%;
                width: 20px;
                height: 20px;
                line-height: 20px;
                text-align: center;
                font-size: 16px;
            }
        }
    }
    
    .top-text {
        font-weight: 500;
        line-height: 1.4;
    }

    // 스피너
    .loader {
        width: 50px;
        height: 50px;
    }
    .loader svg {
        width: 100%;
        height: 100%;
        transform: rotate(-90deg);
    }
    .loader circle {
        fill: none;
        stroke: rgba(36, 124, 255, 0.1);
        stroke-width: 10;
    }
    .loader circle.top {
        stroke: #247CFF;
        stroke-linecap: round;
        stroke-dasharray: 110 220;
        stroke-dashoffset: 0;
        animation: dash 1.6s ease-in-out infinite;
    }
    @keyframes dash {
        0% {
            stroke-dasharray: 5 300;
            stroke-dashoffset: 0;
        }
        50% {
            stroke-dasharray: 180 300;
            stroke-dashoffset: -70;
        }
        100% {
            stroke-dasharray: 5 300;
            stroke-dashoffset: -260;
        }
    }

    .download-button {
        color: #fff;
        background-color: #247CFF;
        border-radius: 5px;
        padding: 4px 16px;
        cursor: pointer;
        border: none;
        
        &:disabled {
            background-color: #ccc;
            cursor: not-allowed;
        }
    }
    
    .result-common-wrapper {
        main { 
            background: #fff; 
        }
    }
    
    // 진행 상황 스타일
    .progress-details {
        .progress-counter {
            font-size: 16px;
            font-weight: 600;
            
            .completed {
                color: #247CFF;
                font-size: 20px;
            }
            .total {
                color: #666;
            }
        }
        
        .progress-dots {
            display: flex;
            gap: 4px;
            justify-content: center;
            flex-wrap: wrap;
            max-width: 400px;
            
            .dot {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: #e0e0e0;
                transition: all 0.3s ease;
                
                &.completed {
                    background-color: #247CFF;
                    transform: scale(1.2);
                }
                
                &.current {
                    background-color: #ff6b35;
                    animation: pulse 1.5s infinite;
                    transform: scale(1.3);
                }
            }
        }
    }
    
    @keyframes pulse {
        0%, 100% {
            opacity: 1;
        }
        50% {
            opacity: 0.5;
        }
    }
</style>